'use client'

import * as React from 'react'

import { environmentManager, MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useSnackbar } from 'notistack'

import { isDevelop } from '@/lib/config.shared'

const STALE_TIME_MS = 5 * 60 * 1000
const BROWSER_GC_TIME_MS = 10 * 60 * 1000

type QueryMeta = {
  errorMessage?: string
  shouldSuppressErrorToast?: (error: unknown) => boolean
}

type MutationMeta = {
  errorMessage?: string
  shouldSuppressErrorToast?: (error: unknown) => boolean
}

export const ReactQueryClientProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            const queryMeta = query.meta as QueryMeta | undefined

            if (queryMeta?.shouldSuppressErrorToast?.(error)) {
              return
            }

            if (queryMeta?.errorMessage) {
              enqueueSnackbar(queryMeta.errorMessage, {
                variant: 'error',
                preventDuplicate: true,
              })
            } else {
              enqueueSnackbar('There was an error from the API', {
                variant: 'error',
                preventDuplicate: true,
              })
            }

            if (isDevelop) {
              console.error('ReactQueryClientProvider.tsx:30', { error, query })
            }
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            const mutationMeta = mutation.meta as MutationMeta | undefined

            if (mutationMeta?.shouldSuppressErrorToast?.(error)) {
              return
            }

            enqueueSnackbar(mutationMeta?.errorMessage ?? 'There was an error updating the API', {
              variant: 'error',
              preventDuplicate: true,
            })

            if (isDevelop) {
              console.error('ReactQueryClientProvider.tsx:56', { error, mutation })
            }
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: STALE_TIME_MS,
            gcTime: environmentManager.isServer() ? Number.POSITIVE_INFINITY : BROWSER_GC_TIME_MS,
            refetchOnWindowFocus: false,
            throwOnError: false, //isDevelop,
          },
          hydrate: {
            deserializeData: JSON.parse,
          },
          dehydrate: {
            serializeData: JSON.stringify,
          },
        },
      })
  )

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
